(() => {
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const controllers = new WeakMap();

  function initHeader(root = document) {
    const header = root.querySelector("[data-site-header]");
    if (!header || controllers.has(header)) return;
    const abort = new AbortController();
    controllers.set(header, abort);
    const options = { signal: abort.signal };
    const mobileMenu = header.querySelector("[data-mobile-menu]");
    const menuOpen = header.querySelector("[data-menu-open]");
    const menuClose = header.querySelector("[data-menu-close]");
    const search = header.querySelector("[data-search-dialog]");
    const searchOpen = header.querySelector("[data-search-open]");
    const searchClose = header.querySelector("[data-search-close]");
    let frame = 0;
    const updateHeader = () => {
      frame = 0;
      header.classList.toggle("site-header-scrolled", window.scrollY > 56);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(updateHeader);
    };
    updateHeader();
    window.addEventListener("scroll", onScroll, { passive: true, signal: abort.signal });

    menuOpen?.addEventListener("click", () => {
      mobileMenu?.showModal();
      menuOpen.setAttribute("aria-expanded", "true");
      mobileMenu?.querySelector("a, button, summary")?.focus();
    }, options);
    menuClose?.addEventListener("click", () => mobileMenu?.close(), options);
    mobileMenu?.querySelectorAll("a").forEach((link) => link.addEventListener("click", () => mobileMenu?.close(), options));
    mobileMenu?.addEventListener("close", () => {
      menuOpen?.setAttribute("aria-expanded", "false");
      menuOpen?.focus();
    }, options);
    searchOpen?.addEventListener("click", () => {
      search?.showModal();
      search?.querySelector("input")?.focus();
    }, options);
    searchClose?.addEventListener("click", () => search?.close(), options);
    [mobileMenu, search].forEach((dialog) => {
      dialog?.addEventListener("click", (event) => {
        if (event.target === dialog) dialog.close();
      }, options);
    });

    header.querySelectorAll("[data-nav-details]").forEach((details) => {
      details.addEventListener("toggle", () => {
        if (!details.open) return;
        header.querySelectorAll("[data-nav-details]").forEach((other) => {
          if (other !== details) other.removeAttribute("open");
        });
      }, options);
    });
    document.addEventListener("click", (event) => {
      if (!header.contains(event.target)) {
        header.querySelectorAll("[data-nav-details][open]").forEach((item) => item.removeAttribute("open"));
      }
    }, options);
  }

  function initReveal(root = document) {
    root.querySelectorAll(".reveal-on-scroll").forEach((element) => {
      if (controllers.has(element)) return;
      if (reducedMotion.matches || !("IntersectionObserver" in window)) {
        element.classList.add("is-visible");
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.12, rootMargin: "0px 0px -8% 0px" });
      observer.observe(element);
      controllers.set(element, { abort: () => observer.disconnect() });
    });
  }

  function initParallax(root = document) {
    if (reducedMotion.matches || window.matchMedia("(max-width: 820px)").matches) return;
    root.querySelectorAll(".parallax-media").forEach((container) => {
      if (controllers.has(container)) return;
      const image = container.querySelector("img");
      if (!image) return;
      let frame = 0;
      let active = false;
      const update = () => {
        frame = 0;
        if (!active) return;
        const rect = container.getBoundingClientRect();
        const progress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
        image.style.transform = `scale(1.06) translate3d(0, ${(progress - .5) * 5}%, 0)`;
      };
      const onScroll = () => {
        if (!frame) frame = requestAnimationFrame(update);
      };
      const visible = new IntersectionObserver(([entry]) => {
        active = entry.isIntersecting;
        if (active) update();
      }, { rootMargin: "20% 0px" });
      visible.observe(container);
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll, { passive: true });
      controllers.set(container, {
        abort: () => {
          visible.disconnect();
          window.removeEventListener("scroll", onScroll);
          window.removeEventListener("resize", onScroll);
          if (frame) cancelAnimationFrame(frame);
        }
      });
    });
  }

  function initMedia(root = document) {
    root.querySelectorAll("video[autoplay]").forEach((video) => {
      if (controllers.has(video)) return;
      const eventController = new AbortController();
      video.muted = true;
      video.defaultMuted = true;
      video.controls = false;
      video.setAttribute("playsinline", "");
      video.setAttribute("webkit-playsinline", "");
      video.removeAttribute("poster");
      if (reducedMotion.matches) {
        video.pause();
        controllers.set(video, eventController);
        return;
      }
      const attemptPlay = () => {
        video.muted = true;
        const promise = video.play();
        if (promise) promise.catch(() => {});
      };
      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          attemptPlay();
        } else if (!video.classList.contains("refined-hero-video")) {
          video.pause();
        }
      }, { rootMargin: "20% 0px" });
      observer.observe(video);
      ["loadeddata", "canplay"].forEach((name) => video.addEventListener(name, attemptPlay, { once: true, signal: eventController.signal }));
      ["pointerdown", "touchstart"].forEach((name) => document.addEventListener(name, attemptPlay, { once: true, passive: true, signal: eventController.signal }));
      window.addEventListener("pageshow", attemptPlay, { signal: eventController.signal });
      document.addEventListener("visibilitychange", () => {
        if (!document.hidden) attemptPlay();
      }, { signal: eventController.signal });
      attemptPlay();
      controllers.set(video, {
        abort: () => {
          eventController.abort();
          observer.disconnect();
        }
      });
    });
  }

  function initGallery(root = document) {
    root.querySelectorAll("[data-product-gallery]").forEach((gallery) => {
      if (controllers.has(gallery)) return;
      const abort = new AbortController();
      controllers.set(gallery, abort);
      const mediaItems = [...gallery.querySelectorAll("[data-media-id]")];
      const controls = [...gallery.querySelectorAll("[data-gallery-target]")];
      const selectMedia = (target) => {
        const targetMedia = mediaItems.find((media) => media.dataset.mediaId === target);
        if (!targetMedia) return;
        mediaItems.forEach((media) => {
          const selected = media === targetMedia;
          media.classList.toggle("is-active", selected);
          media.hidden = !selected;
        });
        controls.forEach((control) => {
          const selected = control.dataset.galleryTarget === target;
          control.classList.toggle("active", selected);
          control.setAttribute("aria-pressed", String(selected));
        });
      };
      controls.forEach((button) => {
        button.addEventListener("click", () => {
          selectMedia(button.dataset.galleryTarget);
        }, { signal: abort.signal });
      });
      gallery.querySelectorAll("[data-gallery-step]").forEach((button) => {
        button.addEventListener("click", () => {
          if (mediaItems.length < 2) return;
          const currentIndex = mediaItems.findIndex((media) => !media.hidden);
          const step = Number(button.dataset.galleryStep);
          const nextIndex = (Math.max(currentIndex, 0) + step + mediaItems.length) % mediaItems.length;
          selectMedia(mediaItems[nextIndex].dataset.mediaId);
        }, { signal: abort.signal });
      });
    });
  }

  function initProductPanels(root = document) {
    root.querySelectorAll("[data-product-root]").forEach((productRoot) => {
      const tabs = [...productRoot.querySelectorAll("[data-product-panel-tab]")];
      if (!tabs.length || tabs.some((tab) => controllers.has(tab))) return;
      const abort = new AbortController();
      tabs.forEach((tab) => controllers.set(tab, abort));
      const select = (tab) => {
        const target = tab.dataset.productPanelTab;
        tabs.forEach((item) => {
          const selected = item === tab;
          item.classList.toggle("active", selected);
          item.setAttribute("aria-selected", String(selected));
        });
        productRoot.querySelectorAll("[data-product-panel]").forEach((panel) => {
          panel.hidden = panel.dataset.productPanel !== target;
        });
      };
      tabs.forEach((tab) => tab.addEventListener("click", () => select(tab), { signal: abort.signal }));
    });
  }

  function initProductBack(root = document) {
    root.querySelectorAll("[data-product-back]").forEach((link) => {
      if (controllers.has(link)) return;
      const abort = new AbortController();
      controllers.set(link, abort);
      link.addEventListener("click", (event) => {
        let referrer;
        try {
          referrer = document.referrer ? new URL(document.referrer) : null;
        } catch (_) {
          referrer = null;
        }
        if (referrer && referrer.origin === window.location.origin && window.history.length > 1) {
          event.preventDefault();
          window.history.back();
        }
      }, { signal: abort.signal });
    });
  }

  function createScrollState(container, scenes, onChange) {
    if (!scenes.length) return { abort() {} };
    let frame = 0;
    let current = -1;
    const update = () => {
      frame = 0;
      const focus = window.innerHeight * 0.5;
      let next = 0;
      let nearest = Infinity;
      scenes.forEach((scene, index) => {
        const rect = scene.getBoundingClientRect();
        const distance = Math.abs((rect.top + rect.height * 0.5) - focus);
        if (distance < nearest) {
          nearest = distance;
          next = index;
        }
        const progress = Math.max(0, Math.min(1, (window.innerHeight - rect.top) / (window.innerHeight + rect.height)));
        scene.style.setProperty("--scene-progress", progress.toFixed(4));
      });
      const rect = container.getBoundingClientRect();
      const total = Math.max(1, rect.height - window.innerHeight);
      const overall = Math.max(0, Math.min(1, -rect.top / total));
      container.style.setProperty("--story-progress", overall.toFixed(4));
      if (next !== current) {
        current = next;
        onChange(next);
      }
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    update();
    return {
      abort() {
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onScroll);
        if (frame) cancelAnimationFrame(frame);
      }
    };
  }

  function initSignatureCinema(root = document) {
    root.querySelectorAll("[data-signature-cinema]").forEach((container) => {
      if (controllers.has(container)) return;
      const scenes = [...container.querySelectorAll("[data-signature-scene]")];
      const current = container.querySelector("[data-signature-current]");
      const progress = container.querySelector(".signature-cinema-progress b");
      const controller = createScrollState(container, scenes, (index) => {
        scenes.forEach((scene, sceneIndex) => scene.classList.toggle("is-active", sceneIndex === index));
        if (current) current.textContent = `0${index + 1}`;
        if (progress) progress.style.transform = `scaleX(${(index + 1) / scenes.length})`;
      });
      controllers.set(container, controller);
    });
  }

  function initHouseExperience(root = document) {
    root.querySelectorAll("[data-house-experience]").forEach((container) => {
      if (controllers.has(container)) return;
      const scenes = [...container.querySelectorAll("[data-house-scene]")];
      const links = [...container.querySelectorAll("[data-house-progress-link]")];
      const counter = container.querySelector("[data-house-counter]");
      const controller = createScrollState(container, scenes, (index) => {
        scenes.forEach((scene, sceneIndex) => scene.classList.toggle("is-active", sceneIndex === index));
        links.forEach((link, linkIndex) => {
          link.classList.toggle("active", linkIndex === index);
          if (linkIndex === index) link.setAttribute("aria-current", "step");
          else link.removeAttribute("aria-current");
        });
        if (counter) counter.textContent = `${String(index + 1).padStart(2, "0")} / ${String(scenes.length).padStart(2, "0")}`;
      });
      controllers.set(container, controller);
    });
  }

  function initCollectionExperience(root = document) {
    root.querySelectorAll("[data-collection-experience]").forEach((container) => {
      if (controllers.has(container)) return;
      const scenes = [...container.querySelectorAll("[data-editorial-scene]")];
      const links = [...container.querySelectorAll("[data-editorial-progress-link]")];
      const counter = container.querySelector("[data-editorial-counter]");
      const progress = container.querySelector("[data-editorial-progress]");
      const timeline = container.querySelector(".collection-editorial-timeline");
      const mobileMotion = window.matchMedia("(max-width: 900px) and (prefers-reduced-motion: no-preference)").matches;
      const abort = new AbortController();
      const selectScene = (index) => {
        scenes.forEach((scene, sceneIndex) => {
          const selected = sceneIndex === index;
          scene.classList.toggle("is-active", selected);
          scene.classList.toggle("is-current", selected);
        });
        links.forEach((link, linkIndex) => {
          const selected = linkIndex === index;
          link.classList.toggle("active", selected);
          if (selected) link.setAttribute("aria-current", "step");
          else link.removeAttribute("aria-current");
        });
        if (counter) counter.textContent = `${String(index + 1).padStart(2, "0")} / ${String(scenes.length).padStart(2, "0")}`;
      };
      let frame = 0;
      const update = () => {
        frame = 0;
        const timelineRect = timeline?.getBoundingClientRect();
        progress?.classList.toggle(
          "is-visible",
          Boolean(timelineRect && timelineRect.top < window.innerHeight * 0.65 && timelineRect.bottom > window.innerHeight * 0.35)
        );

        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;
        scenes.forEach((scene, index) => {
          const rect = scene.getBoundingClientRect();
          const distance = Math.abs(rect.top + rect.height * 0.5 - window.innerHeight * 0.5);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });
        selectScene(closestIndex);
      };
      const requestUpdate = () => {
        if (frame) return;
        frame = requestAnimationFrame(update);
      };

      links.forEach((link) => {
        link.addEventListener("click", (event) => {
          event.preventDefault();
          document.getElementById(link.getAttribute("href")?.slice(1))?.scrollIntoView({
            behavior: reducedMotion.matches ? "auto" : "smooth",
            block: "start"
          });
        }, { signal: abort.signal });
      });

      let revealObserver;
      if (mobileMotion) {
        const groups = scenes.map((scene) => scene.querySelector(".editorial-story-copy")).filter(Boolean);
        container.classList.add("collection-motion-ready");
        groups.forEach((group) => {
          group.classList.add("collection-motion-group");
          [...group.children].forEach((child, index) => {
            child.classList.add("collection-motion-target");
            child.style.setProperty("--collection-motion-order", String(index));
          });
        });

        if ("IntersectionObserver" in window) {
          revealObserver = new IntersectionObserver((entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add("is-revealed");
              entry.target.closest("[data-editorial-scene]")?.classList.add("is-revealed");
              observer.unobserve(entry.target);
            });
          }, { rootMargin: "0px 0px -7% 0px", threshold: 0.12 });
          groups.slice(1).forEach((group) => revealObserver.observe(group));
        } else {
          groups.slice(1).forEach((group) => group.classList.add("is-revealed"));
        }
        requestAnimationFrame(() => groups[0]?.classList.add("is-revealed"));
      }

      window.addEventListener("scroll", requestUpdate, { passive: true });
      window.addEventListener("resize", requestUpdate);
      update();
      controllers.set(container, {
        abort() {
          eventController.abort();
          window.removeEventListener("scroll", requestUpdate);
          window.removeEventListener("resize", requestUpdate);
          if (frame) cancelAnimationFrame(frame);
          revealObserver?.disconnect();
        }
      });
    });
  }

  function initProductSignature(root = document) {
    root.querySelectorAll("[data-product-signature]").forEach((component) => {
      if (controllers.has(component)) return;
      const abort = new AbortController();
      controllers.set(component, abort);
      const productTabs = [...component.querySelectorAll("[data-signature-product-tab]")];
      const productPanels = [...component.querySelectorAll("[data-signature-product-panel]")];
      const selectProduct = (index) => {
        productTabs.forEach((tab, tabIndex) => {
          const selected = tabIndex === index;
          tab.classList.toggle("active", selected);
          tab.setAttribute("aria-selected", String(selected));
          tab.tabIndex = selected ? 0 : -1;
        });
        productPanels.forEach((panel, panelIndex) => {
          const selected = panelIndex === index;
          panel.classList.toggle("active", selected);
          panel.hidden = !selected;
        });
      };
      productTabs.forEach((tab, index) => {
        tab.addEventListener("click", () => selectProduct(index), { signal: abort.signal });
        tab.addEventListener("keydown", (event) => {
          if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) return;
          event.preventDefault();
          let next = index;
          if (event.key === "Home") next = 0;
          else if (event.key === "End") next = productTabs.length - 1;
          else {
            const forward = ["ArrowRight", "ArrowDown"].includes(event.key);
            next = (index + (forward ? 1 : -1) + productTabs.length) % productTabs.length;
          }
          selectProduct(next);
          productTabs[next].focus();
        }, { signal: abort.signal });
      });
      const stageGroups = productPanels.length ? productPanels : [component];
      stageGroups.forEach((group) => {
        const tabs = [...group.querySelectorAll("[data-signature-stage-tab]")];
        const panels = [...group.querySelectorAll("[data-signature-stage]")];
        const selectStage = (index) => {
          tabs.forEach((tab, tabIndex) => {
            const selected = tabIndex === index;
            tab.classList.toggle("active", selected);
            tab.setAttribute("aria-selected", String(selected));
            tab.tabIndex = selected ? 0 : -1;
          });
          panels.forEach((panel, panelIndex) => {
            const selected = panelIndex === index;
            panel.classList.toggle("active", selected);
            panel.hidden = !selected;
          });
        };
        tabs.forEach((tab, index) => {
          tab.addEventListener("click", () => selectStage(index), { signal: abort.signal });
          tab.addEventListener("keydown", (event) => {
            if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)) return;
            event.preventDefault();
            const direction = ["ArrowRight", "ArrowDown"].includes(event.key) ? 1 : -1;
            const next = (index + direction + tabs.length) % tabs.length;
            selectStage(next);
            tabs[next].focus();
          }, { signal: abort.signal });
        });
      });
    });
  }

  function initProduct(root = document) {
    root.querySelectorAll("[data-product-root]").forEach((productRoot) => {
      if (controllers.has(productRoot)) return;
      const abort = new AbortController();
      controllers.set(productRoot, abort);
      const form = productRoot.querySelector("[data-product-form]");
      const json = productRoot.querySelector("[data-product-json]");
      if (!form || !json) return;
      let variants = [];
      try { variants = JSON.parse(json.textContent); } catch (_) { variants = []; }
      const hiddenId = form.querySelector("[data-variant-id]");
      const addButton = form.querySelector("[data-add-to-cart]");
      const addLabel = form.querySelector("[data-add-label]");
      const price = productRoot.querySelector("[data-price]");
      const compare = productRoot.querySelector("[data-compare-price]");
      const money = new Intl.NumberFormat(document.documentElement.lang || "en-GB", { style: "currency", currency: window.Shopify?.currency?.active || "GBP" });

      const updateVariant = () => {
        const selected = [...form.querySelectorAll("[data-option-position]:checked")]
          .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
          .map((input) => input.value);
        const variant = variants.find((item) => item.options.every((value, index) => value === selected[index])) || variants[0];
        if (!variant) return;
        hiddenId.value = variant.id;
        price.textContent = money.format(variant.price / 100);
        if (variant.compare_at_price > variant.price) {
          compare.hidden = false;
          compare.textContent = money.format(variant.compare_at_price / 100);
        } else {
          compare.hidden = true;
        }
        addButton.disabled = !variant.available;
        addLabel.textContent = variant.available ? "Add to bag" : "Sold out";
        const url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        window.history.replaceState({}, "", url);
      };
      form.querySelectorAll("[data-option-position]").forEach((input) => input.addEventListener("change", updateVariant, { signal: abort.signal }));
      form.querySelector("[data-quantity-minus]")?.addEventListener("click", () => {
        const input = form.querySelector('input[name="quantity"]');
        input.value = Math.max(1, Number(input.value) - 1);
      }, { signal: abort.signal });
      form.querySelector("[data-quantity-plus]")?.addEventListener("click", () => {
        const input = form.querySelector('input[name="quantity"]');
        input.value = Math.max(1, Number(input.value) + 1);
      }, { signal: abort.signal });
      form.addEventListener("submit", async (event) => {
        event.preventDefault();
        const error = form.querySelector("[data-product-error]");
        const quantityInput = form.querySelector('input[name="quantity"]');
        const quantityAdded = Math.max(1, Number(quantityInput?.value) || 1);
        error.hidden = true;
        error.textContent = "";
        addButton.disabled = true;
        addButton.setAttribute("aria-busy", "true");
        addLabel.textContent = "Adding…";
        try {
          const formData = new FormData(form);
          const cartDrawer = document.querySelector("cart-drawer");
          if (cartDrawer) {
            formData.append("sections", "cart-drawer");
            formData.append("sections_url", window.location.pathname);
          }
          const response = await fetch(`${window.Shopify?.routes?.root || "/"}cart/add.js`, {
            method: "POST",
            headers: { Accept: "application/json" },
            body: formData
          });
          const payload = await response.json();
          if (!response.ok) throw new Error(payload.description || "This product could not be added.");

          const replaceCartDrawer = (markup) => {
            if (!cartDrawer || !markup) return false;
            const updatedDrawer = new DOMParser().parseFromString(markup, "text/html").querySelector("#CartDrawer");
            const currentDrawer = cartDrawer.querySelector("#CartDrawer");
            if (!updatedDrawer || !currentDrawer) return false;
            currentDrawer.replaceWith(updatedDrawer);
            cartDrawer.classList.remove("is-empty");
            cartDrawer.querySelector("#CartDrawer-Overlay")?.addEventListener("click", cartDrawer.close.bind(cartDrawer));
            return true;
          };

          let drawerUpdated = replaceCartDrawer(payload.sections?.["cart-drawer"]);
          if (cartDrawer && !drawerUpdated) {
            const sectionResponse = await fetch(`${window.Shopify?.routes?.root || "/"}cart?section_id=cart-drawer`, {
              headers: { Accept: "text/html" }
            });
            if (sectionResponse.ok) drawerUpdated = replaceCartDrawer(await sectionResponse.text());
          }

          const renderedCount = drawerUpdated
            ? [...cartDrawer.querySelectorAll('input[name="updates[]"]')]
                .reduce((total, input) => total + Math.max(0, Number(input.value) || 0), 0)
            : null;
          document.querySelectorAll("[data-cart-count]").forEach((count) => {
            const currentCount = Number(count.textContent) || 0;
            count.textContent = String(renderedCount ?? currentCount + quantityAdded);
            count.hidden = false;
          });

          if (cartDrawer && drawerUpdated) {
            cartDrawer.open(addButton);
          } else if (cartDrawer) {
            window.location.assign(`${window.Shopify?.routes?.root || "/"}cart`);
            return;
          }
          addLabel.textContent = "Added to bag";
          addButton.removeAttribute("aria-busy");
          addButton.disabled = false;
          setTimeout(() => { addLabel.textContent = "Add to bag"; }, 900);
        } catch (reason) {
          error.hidden = false;
          error.textContent = reason.message;
          addLabel.textContent = "Add to bag";
          addButton.removeAttribute("aria-busy");
          addButton.disabled = false;
        }
      }, { signal: abort.signal });
    });
  }

  function initComposition(root = document) {
    root.querySelectorAll("[data-composition]").forEach((component) => {
      if (controllers.has(component)) return;
      const abort = new AbortController();
      controllers.set(component, abort);
      component.querySelectorAll("[data-composition-tab]").forEach((tab) => {
        tab.addEventListener("click", () => {
          const index = tab.dataset.compositionTab;
          component.querySelectorAll("[data-composition-tab]").forEach((item) => {
            const selected = item === tab;
            item.classList.toggle("is-active", selected);
            item.setAttribute("aria-selected", String(selected));
          });
          component.querySelectorAll("[data-composition-panel]").forEach((panel) => panel.classList.toggle("is-active", panel.dataset.compositionPanel === index));
          component.querySelectorAll("[data-composition-panel]").forEach((panel) => {
            panel.hidden = panel.dataset.compositionPanel !== index;
          });
        }, { signal: abort.signal });
      });
    });
  }

  function initProductStory(root = document) {
    root.querySelectorAll("[data-product-story]").forEach((container) => {
      if (controllers.has(container)) return;
      const scenes = [...container.querySelectorAll("[data-product-story-scene]")];
      const links = [...container.querySelectorAll("[data-product-story-link]")];
      const counter = container.querySelector("[data-product-story-counter]");
      const progress = container.querySelector(".product-story-progress");
      if (!scenes.length) return;

      if (reducedMotion.matches) {
        scenes.forEach((scene, index) => scene.classList.toggle("is-active", index === 0));
        controllers.set(container, { abort() {} });
        return;
      }

      let frame = 0;
      let current = -1;
      const clamp = (value) => Math.max(0, Math.min(1, value));
      const update = () => {
        frame = 0;
        const viewport = window.innerHeight;
        const desktop = window.matchMedia("(min-width: 901px)").matches;
        let next = 0;
        let nearest = Infinity;

        scenes.forEach((scene, index) => {
          const rect = scene.getBoundingClientRect();
          const distance = Math.abs(rect.top + rect.height * 0.5 - viewport * 0.5);
          if (distance < nearest) {
            nearest = distance;
            next = index;
          }

          if (desktop) {
            const sceneProgress = clamp((viewport - rect.top) / (viewport + rect.height));
            const focus = clamp(1 - Math.abs(sceneProgress - 0.5) * 4);
            scene.style.setProperty("--product-stage-opacity", String(0.64 + focus * 0.36));
            scene.style.setProperty("--product-copy-opacity", String(0.32 + focus * 0.68));
            scene.style.setProperty("--product-copy-y", `${(0.5 - sceneProgress) * 58}px`);
            scene.style.setProperty("--product-media-scale", String(1.09 - sceneProgress * 0.065));
            scene.style.setProperty("--product-media-y", `${(sceneProgress - 0.5) * 4}%`);
          } else {
            scene.style.setProperty("--product-stage-opacity", "1");
            scene.style.setProperty("--product-copy-opacity", "1");
            scene.style.setProperty("--product-copy-y", "0px");
            scene.style.setProperty("--product-media-scale", "1");
            scene.style.setProperty("--product-media-y", "0%");
          }
        });

        if (next !== current) {
          current = next;
          scenes.forEach((scene, index) => scene.classList.toggle("is-active", index === current));
          links.forEach((link, index) => {
            const selected = index === current;
            link.classList.toggle("active", selected);
            if (selected) link.setAttribute("aria-current", "step");
            else link.removeAttribute("aria-current");
          });
          if (counter) counter.textContent = `${String(current + 1).padStart(2, "0")} / ${String(scenes.length).padStart(2, "0")}`;
        }

        if (progress) {
          const timeline = container.querySelector(".product-story-timeline");
          const rect = timeline.getBoundingClientRect();
          progress.classList.toggle("is-visible", desktop && rect.top < viewport * 0.7 && rect.bottom > viewport * 0.3);
        }
      };
      const schedule = () => {
        if (!frame) frame = requestAnimationFrame(update);
      };
      window.addEventListener("scroll", schedule, { passive: true });
      window.addEventListener("resize", schedule, { passive: true });
      update();
      controllers.set(container, {
        abort() {
          window.removeEventListener("scroll", schedule);
          window.removeEventListener("resize", schedule);
          if (frame) cancelAnimationFrame(frame);
        }
      });
    });
  }

  function initSort(root = document) {
    root.querySelectorAll("[data-sort-by]").forEach((select) => {
      if (controllers.has(select)) return;
      const abort = new AbortController();
      controllers.set(select, abort);
      select.addEventListener("change", () => select.form.submit(), { signal: abort.signal });
    });
  }

  function init(root = document) {
    initHeader(root);
    initReveal(root);
    initParallax(root);
    initMedia(root);
    initGallery(root);
    initProduct(root);
    initProductPanels(root);
    initProductBack(root);
    initComposition(root);
    initProductStory(root);
    initSignatureCinema(root);
    initHouseExperience(root);
    initCollectionExperience(root);
    initProductSignature(root);
    initSort(root);
  }

  function cleanup(root) {
    [root, ...root.querySelectorAll("*")].forEach((node) => {
      const controller = controllers.get(node);
      if (controller) {
        controller.abort();
        controllers.delete(node);
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init());
  } else {
    init();
  }
  document.addEventListener("shopify:section:load", (event) => init(event.target));
  document.addEventListener("shopify:section:unload", (event) => cleanup(event.target));
  document.addEventListener("shopify:section:select", (event) => event.target.scrollIntoView({ block: "center", behavior: reducedMotion.matches ? "auto" : "smooth" }));
  document.addEventListener("shopify:block:select", (event) => event.target.scrollIntoView({ block: "center", behavior: reducedMotion.matches ? "auto" : "smooth" }));
})();
